import React, { useEffect, useState, useCallback } from "react";
import * as Styled from "./ManageProfilesComponent.styled";
import { useNavigate } from "react-router-dom";
import { blocksService } from "../../services";
import { Loader } from "../ui";
import { BaseButton, ConfirmModal, IProfile } from "../../libs";
import SearchIcon from "../../assets/images/icons/search.svg";
import { toast } from "react-toastify";
import { Plus } from "react-bootstrap-icons";
import { ProfileCard } from "./profile-card";
import { confirmAlert } from "react-confirm-alert";
import { NewProfileAlert } from "./new-profile-alert";

export const ManageProfilesComponent: React.FC = () => {
    const navigate = useNavigate();
    const [profiles, setProfiles] = useState<IProfile[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");

    const fetchProfiles = useCallback(async () => {
        setLoading(true);
        try {
            const data = await blocksService.getProfiles();
            setProfiles(data);
        } catch (error) {
            console.error("Error fetching profiles:", error);
            toast.error("Failed to load profiles");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchProfiles();
    }, [fetchProfiles]);

    const filteredData = React.useMemo(() => {
        return profiles.filter(item =>
            item.profileName?.toLowerCase().includes(searchQuery.toLowerCase()) ?? true
        );
    }, [searchQuery, profiles]);

    const handleProfileClick = (profile: IProfile) => {
        navigate(`/manage-libraries/manage-profiles/${profile.profileId}/versions`, { state: { profile } });
    };

    const handleCreateProfile = () => {
        confirmAlert({
            customUI: ({ onClose }) => {
                return (
                    <NewProfileAlert
                        onClose={onClose}
                        handleSaveProfile={async (name: string) => {
                            try {
                                await blocksService.createProfile({ profileName: name });
                                toast.success("Profile created successfully");
                                await fetchProfiles();
                            } catch (error) {
                                console.error("Error creating profile:", error);
                                toast.error("Failed to create profile");
                            }
                        }}
                    />
                );
            },
            closeOnEscape: true,
            closeOnClickOutside: false,
        });
    };

    const showEditModal = React.useCallback((profile: IProfile) => {
        confirmAlert({
            customUI: ({ onClose }) => {
                return (
                    <NewProfileAlert
                        onClose={onClose}
                        handleSaveProfile={async (name: string) => {
                            try {
                                await blocksService.updateProfile(profile.profileId, { profileId: profile.profileId, profileName: name });
                                toast.success("Profile updated successfully");
                                await fetchProfiles();
                            } catch (error) {
                                console.error("Error updating profile:", error);
                                toast.error("Failed to update profile");
                            }
                        }}
                        initialProfileName={profile.profileName}
                        isEdit={true}
                    />
                );
            },
            closeOnEscape: true,
            closeOnClickOutside: false,
        });
    }, [fetchProfiles]);

    const handleInvalidateCache = React.useCallback((profile: IProfile) => {
        confirmAlert({
            customUI: ({ onClose }) => (
                <ConfirmModal
                    title="Invalidate Cache"
                    description={[
                        { text: `Clear the cached embed data for "${profile.profileName}"?` },
                        { text: "The next page load will fetch fresh data from the database." },
                    ]}
                    noText="Cancel"
                    yesText="Invalidate"
                    onNo={onClose}
                    onYes={async () => {
                        onClose();
                        try {
                            await blocksService.invalidateProfileCache(profile.clientId, profile.profileId);
                            toast.success("Cache invalidated successfully");
                        } catch (error) {
                            console.error("Error invalidating cache:", error);
                            toast.error("Failed to invalidate cache");
                        }
                    }}
                />
            ),
            closeOnEscape: true,
            closeOnClickOutside: false,
        });
    }, []);

    const handleProfileActivationToggle = React.useCallback(async (profile: IProfile, enable: boolean) => {
        try {
            const updatedProfile = await blocksService.updateProfile(profile.profileId, { profileId: profile.profileId, profileName: profile.profileName, active: enable });
            toast.success(`Profile ${enable ? "enabled" : "disabled"} successfully`);
            setProfiles(prev => prev.map(p => p.profileId === profile.profileId ? { ...p, ...updatedProfile, profileName: updatedProfile.profileName || p.profileName, active: enable } : p));
        } catch (error) {
            console.error(`Error ${enable ? "enabling" : "disabling"} profile:`, error);
            toast.error(`Failed to ${enable ? "enable" : "disable"} profile`);
        }
    }, []);

    return loading ? (
        <Loader />
    ) : (
        <>
            <Styled.StyledContainer>
                <Styled.Header>
                    <Styled.HeaderLeftSection>
                        Manage Profiles
                    </Styled.HeaderLeftSection>
                    <Styled.HeaderRightSection>
                        <Styled.DashboardSearchWrapper>
                            <Styled.SearchBarWrapper>
                                <Styled.SearchIcon src={SearchIcon} alt="Search" />
                                <Styled.SearchInput
                                    name="search"
                                    type="text"
                                    placeholder="Search by name"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </Styled.SearchBarWrapper>
                        </Styled.DashboardSearchWrapper>
                        <BaseButton onClick={handleCreateProfile}><Styled.NewProfileWrapper>
                            <Plus size={28} />
                            New Profile
                        </Styled.NewProfileWrapper></BaseButton>
                    </Styled.HeaderRightSection>
                </Styled.Header>

                <Styled.Description>
                    Manage your content profiles. Create profiles to organize your library templates and content blocks.
                </Styled.Description>
                {
                    filteredData.length === 0 ? (
                        <Styled.NoDataWrapper>
                            No profiles found. Create a new profile to get started.
                        </Styled.NoDataWrapper>
                    ) : filteredData.map((profile) => (
                        <ProfileCard
                            key={profile.profileId}
                            title={profile.profileName}
                            subtitle={`${profile.versionCount || 0} versions`}
                            isLinked={true}
                            isEnabled={profile.active}
                            onChevronRightClick={() => handleProfileClick(profile)}
                            onEdit={() => {
                                showEditModal(profile);
                            }}
                            onToggleEnabled={(enabled) => {
                                handleProfileActivationToggle(profile, enabled);
                            }}
                            onInvalidateCache={() => handleInvalidateCache(profile)}
                        />
                    ))
                }
            </Styled.StyledContainer>
        </>
    );
};
